import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

function AllocationDetailsModal({ course, academicSessionId, focus, onClose }) {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/paper-allocations/by-course/${course.CourseID}/${academicSessionId}`);
      if (res.data.success) setAllocations(res.data.data);
    } catch {
      toast.error('Failed to load allocation details');
    } finally {
      setLoading(false);
    }
  }, [course.CourseID, academicSessionId]);

  useEffect(() => { load(); }, [load]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const renderStatus = (status) => {
    const s = (status || 'Pending').toLowerCase();
    const cls = s === 'approved' || s === 'submitted' ? 'badge-active' : s === 'rejected' ? 'badge-inactive' : 'badge-pending';
    return <span className={`badge ${cls}`}>{status || 'Pending'}</span>;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" style={{ maxWidth: 1000, maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Allocation Details</h2>
            <div style={{ marginTop: 4, fontSize: 13, color: '#616161' }}>
              <strong>{course.CourseCode}</strong> &middot; {course.CourseName}
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
        ) : allocations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            No allocations yet for this course.
          </div>
        ) : (
          allocations.map((a) => (
            <div key={a.AllocationID} className="alloc-details-block">
              <div className="alloc-details-header">
                <div>
                  <h3 style={{ margin: 0 }}>{a.ExamTypeName}</h3>
                  <div style={{ fontSize: 12, color: '#616161', marginTop: 4 }}>
                    Setter Due: <strong>{formatDate(a.SetterDueDate)}</strong> &nbsp;|&nbsp;
                    Moderator Due: <strong>{formatDate(a.ModeratorDueDate)}</strong> &nbsp;|&nbsp;
                    Approval: {renderStatus(a.ApprovalStatus)}
                  </div>
                </div>
              </div>

              {/* Sets */}
              {(focus === 'all' || focus === 'setters' || focus === 'moderators') && (
                <div style={{ marginTop: 12 }}>
                  <div className="alloc-details-subtitle">Paper Sets</div>
                  {a.sets && a.sets.length > 0 ? (
                    <table className="data-table" style={{ marginTop: 8 }}>
                      <thead>
                        <tr>
                          <th>Set</th>
                          <th>Setter</th>
                          <th>Setter Status</th>
                          <th>Moderator</th>
                          <th>Moderator Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.sets.map((s) => (
                          <tr key={s.AssignmentID}>
                            <td><strong>{s.SetName}</strong></td>
                            <td>
                              <div>{s.SetterName}</div>
                              <div style={{ fontSize: 12, color: '#999' }}>{s.SetterEmail}</div>
                            </td>
                            <td>{renderStatus(s.SetterStatus)}</td>
                            <td>
                              {s.ModeratorName ? (
                                <>
                                  <div>{s.ModeratorName}</div>
                                  <div style={{ fontSize: 12, color: '#999' }}>{s.ModeratorEmail}</div>
                                </>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                            <td>{s.ModeratorUserID ? renderStatus(s.ModeratorStatus) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ color: '#999', padding: 8 }}>No sets assigned.</div>
                  )}
                </div>
              )}

              {/* Valuators */}
              {(focus === 'all' || focus === 'valuators') && (
                <div style={{ marginTop: 16 }}>
                  <div className="alloc-details-subtitle">Valuators Panel</div>
                  {a.valuators && a.valuators.length > 0 ? (
                    <div className="valuators-chips">
                      {a.valuators.map((v) => (
                        <div key={v.ValuatorID} className="valuator-chip">
                          <div>
                            <strong>{v.FullName}</strong>
                            <div style={{ fontSize: 11, color: '#999' }}>{v.Email}</div>
                          </div>
                          {renderStatus(v.ValuationStatus)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#999', padding: 8 }}>No valuators assigned.</div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AllocationDetailsModal;
